import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField
} from '@material-ui/core';
import { Sparkles } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useTranslation } from '../../../../../../i18next';

type AIMultiBlockGeneratorState = 'idle' | 'generating' | 'inserting' | 'error';

interface AIMultiBlockGeneratorDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (prompt: string) => Promise<void>;
  state: AIMultiBlockGeneratorState;
  error?: string;
}

export const AIMultiBlockGeneratorDialog: React.FC<
  AIMultiBlockGeneratorDialogProps
> = ({ open, onClose, onGenerate, state, error }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');

  // Reset prompt when dialog opens
  useEffect(() => {
    if (open) {
      setPrompt('');
    }
  }, [open]);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return;
    await onGenerate(prompt);
  }, [prompt, onGenerate]);

  const isGenerating = state === 'generating' || state === 'inserting';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitleWithTypography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={20} />
          <span>Generate Content with AI</span>
        </div>
      </DialogTitleWithTypography>
      <DialogContent>
        <FormRow>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            label="What content would you like to generate?"
            placeholder="E.g., Create a welcome section for new employees with a heading, introduction text, and key policies..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={isGenerating}
            autoFocus
            error={state === 'error'}
            helperText={
              error ||
              'The AI will generate multiple content blocks based on your description.'
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                handleGenerate();
              }
            }}
          />
        </FormRow>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isGenerating}>
          {t('Cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleGenerate}
          disabled={!prompt.trim() || isGenerating}
          startIcon={
            isGenerating ? <CircularProgress size={16} /> : <Sparkles size={16} />
          }
        >
          {isGenerating ? 'Generating...' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Hook to manage AI multi-block generation state
 *
 * This simulates what would be a multi-agent system in production:
 * 1. Content planner agent determines what blocks to create
 * 2. Individual content-type agents generate specific block content
 * 3. Blocks are returned in optimal order for insertion
 */
export const useAIMultiBlockGenerator = <T extends { id: string }>(
  onInsertBlocks: (blocks: T[]) => void
) => {
  const [state, setState] = useState<AIMultiBlockGeneratorState>('idle');
  const [error, setError] = useState<string>();

  const generateAndInsert = useCallback(
    async (
      prompt: string,
      generateBlocks: (prompt: string) => Promise<T[]>
    ) => {
      try {
        setState('generating');
        setError(undefined);

        // Simulate the content planner agent deciding what blocks to create
        const blocks = await generateBlocks(prompt);

        setState('inserting');

        // Insert blocks progressively to create AI "building" effect
        // In production, each block would be generated by its specialized agent
        await insertBlocksProgressively(blocks, onInsertBlocks);

        setState('idle');
      } catch (err) {
        setState('error');
        setError(
          err instanceof Error ? err.message : 'Failed to generate content'
        );
      }
    },
    [onInsertBlocks]
  );

  return {
    state,
    error,
    generateAndInsert,
    resetState: () => {
      setState('idle');
      setError(undefined);
    }
  };
};

/**
 * Inserts blocks one by one with a delay between each
 * This creates the effect of AI "building" the content progressively
 *
 * In production, this would coordinate with individual content-type agents:
 * - Each agent generates its block independently
 * - Blocks stream in as they complete
 * - UI shows progressive construction
 */
async function insertBlocksProgressively<T extends { id: string }>(
  blocks: T[],
  onInsertBlocks: (blocks: T[]) => void,
  delayMs: number = 600
): Promise<void> {
  for (let i = 0; i < blocks.length; i++) {
    console.log(`[AI Generator] Inserting block ${i + 1}/${blocks.length}`, blocks[i]);

    // Insert one block at a time
    onInsertBlocks([blocks[i]]);

    // Delay before inserting next block (except after the last one)
    if (i < blocks.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  console.log('[AI Generator] All blocks inserted');
}
